// ─── Manual Floorplan Model ───────────────────────────────────────────────────
// Deterministic threshold-based floorplan cost math.
// Fees are ONE-TIME per unit, charged when the vehicle hits each threshold.
// Interest accrues daily on outstanding principal (reduced by curtailments).

export interface LenderTerms {
  period1Days: number;
  period2Days: number;
  period3Days: number;
  period4Days: number;

  aprRate: number;        // annual percent, e.g. 18.99

  feePeriod1: number;     // $ charged at day 0 per unit
  feePeriod2: number;     // $ charged when unit passes T2
  feePeriod3: number;     // $ charged when unit passes T3
  feePeriod4: number;     // $ charged when unit passes T4 (optional)

  curtailment1Type: "percent" | "dollar";
  curtailment1Value: number;  // percent (0-100) or fixed $

  curtailment2Type: "percent" | "dollar";
  curtailment2Value: number;
}

export interface ScenarioInputs {
  vehicleValue: number;   // principal per unit ($)
  daysOnFloor: number;    // D
  avgUnitsPerMonth: number;
}

export interface ThresholdResult {
  T1: number;
  T2: number;
  T3: number;
  T4: number;
  hasT4: boolean;
}

export interface CostBreakdown {
  interestTotal: number;
  feesTotal: number;
  trueCostPerUnit: number;
  effectiveAprPct: number;
  avgBalancePerUnit: number;
  projectedAnnualCost: number;
  annualUnits: number;

  feeApplied1: boolean;
  feeApplied2: boolean;
  feeApplied3: boolean;
  feeApplied4: boolean;

  feeTriggerExplanations: string[];

  segment1Days: number;
  segment2Days: number;
  segment3Days: number;

  principal: number;
  principalAfterT2: number;
  principalAfterT3: number;

  curtailment1Amt: number;
  curtailment2Amt: number;
}

export function computeThresholds(terms: Pick<LenderTerms, "period1Days" | "period2Days" | "period3Days" | "period4Days">): ThresholdResult {
  const { period1Days: P1, period2Days: P2, period3Days: P3, period4Days: P4 } = terms;
  return {
    T1: 0,
    T2: P1,
    T3: P1 + P2,
    T4: P1 + P2 + P3,
    hasT4: P4 > 0,
  };
}

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

export function computeCostBreakdown(terms: LenderTerms, scenario: ScenarioInputs): CostBreakdown {
  const { vehicleValue: principal, daysOnFloor: D, avgUnitsPerMonth } = scenario;
  const { T1, T2, T3, T4, hasT4 } = computeThresholds(terms);
  const {
    aprRate, feePeriod1, feePeriod2, feePeriod3, feePeriod4,
    curtailment1Type, curtailment1Value,
    curtailment2Type, curtailment2Value,
  } = terms;

  // ─── Curtailment amounts ────────────────────────────────────────────────────
  const curt1Amt = curtailment1Type === "percent"
    ? clamp(principal * curtailment1Value / 100, 0, principal)
    : clamp(curtailment1Value, 0, principal);

  const principalAfterT2 = clamp(principal - curt1Amt, 0, principal);

  const curt2Amt = curtailment2Type === "percent"
    ? clamp(principalAfterT2 * curtailment2Value / 100, 0, principalAfterT2)
    : clamp(curtailment2Value, 0, principalAfterT2);

  const principalAfterT3 = clamp(principalAfterT2 - curt2Amt, 0, principalAfterT2);

  // ─── Fee triggers ──────────────────────────────────────────────────────────
  const feeApplied1 = true;
  const feeApplied2 = D > T2;
  const feeApplied3 = D > T3;
  const feeApplied4 = hasT4 && D > T4;

  let feesTotal = feePeriod1;
  if (feeApplied2) feesTotal += feePeriod2;
  if (feeApplied3) feesTotal += feePeriod3;
  if (feeApplied4) feesTotal += feePeriod4;

  const feeTriggerExplanations: string[] = [];
  feeTriggerExplanations.push(`Fee 1 ($${feePeriod1.toFixed(2)}) — charged at day 0 (always)`);
  if (feeApplied2) feeTriggerExplanations.push(`Fee 2 ($${feePeriod2.toFixed(2)}) — triggered at day ${T2}`);
  else if (feePeriod2 > 0) feeTriggerExplanations.push(`Fee 2 ($${feePeriod2.toFixed(2)}) — NOT triggered (unit sold before day ${T2})`);
  if (feeApplied3) feeTriggerExplanations.push(`Fee 3 ($${feePeriod3.toFixed(2)}) — triggered at day ${T3}`);
  else if (feePeriod3 > 0) feeTriggerExplanations.push(`Fee 3 ($${feePeriod3.toFixed(2)}) — NOT triggered (unit sold before day ${T3})`);
  if (hasT4) {
    if (feeApplied4) feeTriggerExplanations.push(`Fee 4 ($${feePeriod4.toFixed(2)}) — triggered at day ${T4}`);
    else if (feePeriod4 > 0) feeTriggerExplanations.push(`Fee 4 ($${feePeriod4.toFixed(2)}) — NOT triggered (unit sold before day ${T4})`);
  }

  // ─── Interest by segment ───────────────────────────────────────────────────
  const dailyRate = aprRate / 100 / 365;

  const seg1Days = Math.max(0, Math.min(D, T2) - T1);
  const seg2Days = D > T2 ? Math.max(0, Math.min(D, T3) - T2) : 0;
  const seg3Days = D > T3 ? Math.max(0, D - T3) : 0;

  const interestTotal =
    principal * dailyRate * seg1Days +
    principalAfterT2 * dailyRate * seg2Days +
    principalAfterT3 * dailyRate * seg3Days;

  // ─── True cost per unit ────────────────────────────────────────────────────
  const trueCostPerUnit = interestTotal + feesTotal;

  // ─── Effective APR (time-weighted) ─────────────────────────────────────────
  const avgBalancePerUnit = D > 0
    ? (principal * seg1Days + principalAfterT2 * seg2Days + principalAfterT3 * seg3Days) / D
    : principal;

  const effectiveAprPct = D > 0 && avgBalancePerUnit > 0
    ? (trueCostPerUnit / avgBalancePerUnit) * (365 / D) * 100
    : 0;

  // ─── Annual projection — based on this receivable's own turn time ──────────
  const annualUnits = D > 0 ? 365 / D : 0;
  const projectedAnnualCost = trueCostPerUnit * annualUnits;

  return {
    interestTotal,
    feesTotal,
    trueCostPerUnit,
    effectiveAprPct,
    avgBalancePerUnit,
    projectedAnnualCost,
    annualUnits,
    feeApplied1,
    feeApplied2,
    feeApplied3,
    feeApplied4,
    feeTriggerExplanations,
    segment1Days: seg1Days,
    segment2Days: seg2Days,
    segment3Days: seg3Days,
    principal,
    principalAfterT2,
    principalAfterT3,
    curtailment1Amt: curt1Amt,
    curtailment2Amt: curt2Amt,
  };
}

// Turn sensitivity: compute cost at various days on floor
export function computeTurnSensitivityModel(
  terms: LenderTerms,
  scenario: ScenarioInputs,
  daysRange: number[] = [10, 20, 30, 45, 60, 75, 90, 120],
): Array<{ days: number; costPerUnit: number; effectiveApr: number }> {
  return daysRange.map((days) => {
    const bd = computeCostBreakdown(terms, { ...scenario, daysOnFloor: days });
    return {
      days,
      costPerUnit: bd.trueCostPerUnit,
      effectiveApr: bd.effectiveAprPct,
    };
  });
}

// Build default LenderTerms (blank)
export function defaultTerms(): LenderTerms {
  return {
    period1Days: 30,
    period2Days: 30,
    period3Days: 30,
    period4Days: 0,
    aprRate: 0,
    feePeriod1: 0,
    feePeriod2: 0,
    feePeriod3: 0,
    feePeriod4: 0,
    curtailment1Type: "percent",
    curtailment1Value: 0,
    curtailment2Type: "percent",
    curtailment2Value: 0,
  };
}

// Parse lender DB record to LenderTerms (all fields are strings/null in DB)
export function dbLenderToTerms(lender: any): LenderTerms {
  const n = (v: any, def = 0) => v != null ? parseFloat(v) : def;
  return {
    period1Days: n(lender.period1Days, 30),
    period2Days: n(lender.period2Days, 30),
    period3Days: n(lender.period3Days, 30),
    period4Days: n(lender.period4Days, 0),
    aprRate: n(lender.aprRate, 0),
    feePeriod1: n(lender.feePeriod1, 0),
    feePeriod2: n(lender.feePeriod2, 0),
    feePeriod3: n(lender.feePeriod3, 0),
    feePeriod4: n(lender.feePeriod4, 0),
    curtailment1Type: (lender.curtailment1Type as "percent" | "dollar") ?? "percent",
    curtailment1Value: n(lender.curtailment1Value, 0),
    curtailment2Type: (lender.curtailment2Type as "percent" | "dollar") ?? "percent",
    curtailment2Value: n(lender.curtailment2Value, 0),
  };
}

export function lenderHasTerms(lender: any): boolean {
  return lender.period1Days != null;
}

// ─── Tiered Pricing ───────────────────────────────────────────────────────────

export interface TierConfig {
  threshold: number;   // $ — vehicle value must be >= threshold to use this tier
  terms: LenderTerms;
}

export function dbTiersToTierConfigs(tiers: any[]): TierConfig[] {
  const n = (v: any, def = 0) => v != null ? parseFloat(v) : def;
  return tiers
    .map((t) => ({
      threshold: parseFloat(t.threshold),
      terms: {
        period1Days: n(t.period1Days, 30),
        period2Days: n(t.period2Days, 30),
        period3Days: n(t.period3Days, 30),
        period4Days: n(t.period4Days, 0),
        aprRate: n(t.aprRate, 0),
        feePeriod1: n(t.feePeriod1, 0),
        feePeriod2: n(t.feePeriod2, 0),
        feePeriod3: n(t.feePeriod3, 0),
        feePeriod4: n(t.feePeriod4, 0),
        curtailment1Type: (t.curtailment1Type as "percent" | "dollar") ?? "percent",
        curtailment1Value: n(t.curtailment1Value, 0),
        curtailment2Type: (t.curtailment2Type as "percent" | "dollar") ?? "percent",
        curtailment2Value: n(t.curtailment2Value, 0),
      },
    }));
}

export interface CostBreakdownResult {
  breakdown: CostBreakdown;
  /** -1 = standard terms, 0+ = index of matched tier */
  tierIndex: number;
  /** true when any tier matched (backward-compat) */
  isFlexTier: boolean;
}

/**
 * Selects the most specific matching tier (highest threshold <= vehicleValue),
 * falls back to standard terms if none match.
 */
export function computeCostBreakdownWithTiers(
  standardTerms: LenderTerms,
  tiers: TierConfig[],
  scenario: ScenarioInputs,
): CostBreakdownResult {
  if (tiers.length > 0) {
    const sorted = tiers
      .map((t, i) => ({ ...t, idx: i }))
      .sort((a, b) => b.threshold - a.threshold);
    const matched = sorted.find((t) => scenario.vehicleValue >= t.threshold);
    if (matched) {
      return {
        breakdown: computeCostBreakdown(matched.terms, scenario),
        tierIndex: matched.idx,
        isFlexTier: true,
      };
    }
  }
  return { breakdown: computeCostBreakdown(standardTerms, scenario), tierIndex: -1, isFlexTier: false };
}

// ─── Backward-compat shim ─────────────────────────────────────────────────────
export interface FlexConfig {
  enabled: boolean;
  threshold: number;
  terms: LenderTerms;
}

/** @deprecated Use dbTiersToTierConfigs + computeCostBreakdownWithTiers instead */
export function lenderFlexConfig(lender: any): FlexConfig | null {
  const tiers: any[] = lender.tiers ?? [];
  if (tiers.length === 0) return null;
  const sorted = [...tiers].sort((a, b) => parseFloat(b.threshold) - parseFloat(a.threshold));
  const first = sorted[0];
  const threshold = parseFloat(first.threshold || "0") || 0;
  if (threshold <= 0) return null;
  const n = (v: any, def = 0) => v != null ? parseFloat(v) : def;
  return {
    enabled: true,
    threshold,
    terms: {
      period1Days: n(first.period1Days, 30),
      period2Days: n(first.period2Days, 30),
      period3Days: n(first.period3Days, 30),
      period4Days: n(first.period4Days, 0),
      aprRate: n(first.aprRate, 0),
      feePeriod1: n(first.feePeriod1, 0),
      feePeriod2: n(first.feePeriod2, 0),
      feePeriod3: n(first.feePeriod3, 0),
      feePeriod4: n(first.feePeriod4, 0),
      curtailment1Type: (first.curtailment1Type as "percent" | "dollar") ?? "percent",
      curtailment1Value: n(first.curtailment1Value, 0),
      curtailment2Type: (first.curtailment2Type as "percent" | "dollar") ?? "percent",
      curtailment2Value: n(first.curtailment2Value, 0),
    },
  };
}

/** @deprecated Use computeCostBreakdownWithTiers instead */
export function computeCostBreakdownWithFlex(
  standardTerms: LenderTerms,
  flexConfig: FlexConfig | null,
  scenario: ScenarioInputs,
): CostBreakdownResult {
  if (flexConfig && flexConfig.enabled && flexConfig.threshold > 0) {
    return computeCostBreakdownWithTiers(
      standardTerms,
      [{ threshold: flexConfig.threshold, terms: flexConfig.terms }],
      scenario,
    );
  }
  return { breakdown: computeCostBreakdown(standardTerms, scenario), tierIndex: -1, isFlexTier: false };
}
